import { inject, Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    Route,
    RouterStateSnapshot,
    UrlSegment,
    UrlTree,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { QuoteControllerSelectors } from 'sharedStores/quote-controller/quote-controller.module';
import { AppConfigService } from 'sharedServices/app-config.service';
import { ResolveStoreValueService } from 'sharedServices/resolve-store-value.service';
import { selectError } from 'sharedStores/error/error.selectors';
import { isPlatformBrowser } from '@angular/common';
import { selectLeadNumber } from 'stores/quote/quote.selectors';
import { Condition } from 'sharedStores/cms/models/condition';
import { DynamicPageData } from 'sharedModels/dynamic-page-data';
import { LoggingService } from 'shared/base/generics/services/logging.service';
import { clearError } from 'sharedStores/error/error.actions';
import { generateRouteFromUrl } from 'sharedHelpers/route-helper';
import {
    NavigationService_Token,
    QuoteControllerService_Token,
} from 'shared/base/generics/injection-tokens/dependency-tokens';

type CanActivateReturnType =
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree;

@Injectable({
    providedIn: 'root',
})
export class RouteControllerGuard {
    public quoteController = inject(QuoteControllerService_Token);
    public navigationService = inject(NavigationService_Token);

    constructor(
        private readonly store$: Store,
        private readonly appConfig: AppConfigService,
        private readonly resolveStoreValue$: ResolveStoreValueService,
        private readonly loggingService$: LoggingService,
        @Inject(PLATFORM_ID) private readonly platformId: Object,
    ) {}

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): CanActivateReturnType {
        let canActivate = true;
        const env = this.appConfig.config();
        const leadNumber = this.store$.selectSignal(selectLeadNumber)();

        if (!leadNumber) {
            route.queryParams = null;
            return canActivate;
        }

        if (route.queryParams.debug === env?.debugId) {
            env.debugDrawer = 'true';
            this.appConfig.config.set(env);
        }

        const dynamicPageData = this.store$.selectSignal(
            QuoteControllerSelectors.selectPageByRoute({
                route: generateRouteFromUrl(this.getResolvedUrl(route), this.platformId),
            }),
        )();

        const hasError = this.checkErrorState();
        if (hasError) {
            return false;
        }

        if (isPlatformBrowser(this.platformId)) {
            const navigationResult = this.navigationService.resolveConditionalPageActivation(
                dynamicPageData.page,
            );

            canActivate = navigationResult.canActivate;
            if (!canActivate) {
                this.quoteController.navigate(navigationResult.route);
                return canActivate;
            }

            // NOTE: Following page condition checks will be removed once we have all page conditions moved into the NavigationService
            // If we return any page conditions from the CMS, use them to see if we can access this route or not
            canActivate = this.deprecatedPageConditionCheck(dynamicPageData, canActivate);
        }

        return canActivate;
    }

    private deprecatedPageConditionCheck(dynamicPageData: DynamicPageData, canActivate: boolean) {
        if (dynamicPageData?.page?.condition) {
            if (!this.appConfig.config().production) {
                console.error(
                    'Page condition is deprecated, use NavigationService instead',
                    dynamicPageData.page.name,
                );
            }

            const { statePropertyPaths, code } = dynamicPageData.page.condition;
            const compareFunction = code?.[0]?.data?.code;

            if (statePropertyPaths && compareFunction) {
                canActivate = this.resolveStoreValue$.resolveValueSignal(
                    null,
                    dynamicPageData.page.condition,
                );
                if (!canActivate) {
                    this.handleNavigation(dynamicPageData.page.condition);
                }
            }
        }
        return canActivate;
    }

    handleNavigation(pageCondition: Condition) {
        //Check for conditional routing condition on page condition
        if (pageCondition.conditionalRouteCode?.length > 0) {
            const route = this.resolveStoreValue$.resolveConditionalRoute(pageCondition, true);
            this.quoteController.navigate(route);
            return;
        }
        if (pageCondition.routeArray?.length > 0) {
            this.quoteController.navigate(pageCondition.routeArray[0].route);
            return;
        }
        this.quoteController.navigate(null);
    }

    private checkErrorState() {
        // If the app is in error state always ensure we show the error dialog to
        // ensure the user is not able to continue with the app in an invalid state
        // Update this seems to be blocking users uneccessarily, so we will log the error and return false
        // This is to ensure that the error state is logged, and we can investigate the issue
        const errorState = this.store$.selectSignal(selectError)();
        if (errorState != null) {
            this.loggingService$.log({
                message:
                    'Error state detected in store in route-controller.guard - ' +
                    JSON.stringify(errorState),
                level: 'Warn',
                tags: ['error'],
                additionalInfo: [{ name: 'error', value: errorState }],
            });
            // Clear the error so that we don't keep logging it over and over again
            this.store$.dispatch(clearError());
        }

        return false;
    }

    canActivateChild(
        childRoute: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): CanActivateReturnType {
        return true;
    }

    canDeactivate(
        component: unknown,
        currentRoute: ActivatedRouteSnapshot,
        currentState: RouterStateSnapshot,
        nextState?: RouterStateSnapshot,
    ): CanActivateReturnType {
        return true;
    }

    canLoad(route: Route, segments: UrlSegment[]): CanActivateReturnType {
        return true;
    }

    private getResolvedUrl(route: ActivatedRouteSnapshot): string {
        return route.pathFromRoot
            .filter((snapshot) => snapshot.url && snapshot.url.length > 0)
            .map((snapshot) => {
                return snapshot.url
                    .map((segment) => {
                        return segment.path;
                    })
                    .join('/');
            })
            .join('/');
    }
}
